export interface AgentTraceEvent {
  type: string
  label?: string
  summary?: string
  message?: string
  detail?: string
  node?: string
  status?: string
  source?: string
  tool?: string
  tools?: string[]
  toolCallIds?: string[]
  input?: unknown
  output?: unknown
  subgraph?: string
  at?: number
}

export interface AgentPendingConfirmation {
  type: 'tool_confirmation_required'
  message: string
  tools: Array<{ name: string; id: string; args?: Record<string, unknown> }>
}

export interface AgentTimelineEvent {
  kind: 'reasoning' | 'trace' | 'answer'
  seq?: number
  text?: string
  trace?: AgentTraceEvent
}

export interface AgentContextWindow {
  usedTokens: number
  limitTokens: number
  remainingTokens: number
  usedPercent: number
  source: 'summarize'
  mode: 'dev'
}

export interface AiMessage {
  /** 服务端投影或本地请求生成的稳定消息 ID，用于刷新/切换任务时精确归并。 */
  id?: string
  /** 新消息在服务端确认前只保留当前本地快照；旧缓存没有该状态，不参与未同步消息保留。 */
  syncState?: 'pending' | 'synced'
  role: 'user' | 'assistant'
  text: string
  trace?: AgentTraceEvent[]
  /** 推理摘要与工具事件按服务端 SSE 到达顺序组成的展示时间线。 */
  timeline?: AgentTimelineEvent[]
  topics?: Array<Record<string, unknown>>
  /** 内容生成工具返回的原始业务对象；前端只负责按 kind 解析成卡片。 */
  creation?: Record<string, unknown>
  studioWrite?: Record<string, unknown>
  workspaceLinks?: Array<Record<string, unknown>>
  error?: boolean
  retryable?: boolean
  status?: 'running' | 'completed' | 'unavailable' | 'empty' | 'partial' | 'error' | 'interrupted'
  pendingConfirmation?: AgentPendingConfirmation
  feedback?: { rating: 'up' | 'down'; reason?: string; otherReason?: string }
  runId?: string
  runStartedAt?: number
  runFinishedAt?: number
  runDurationMs?: number
  /** summarize 节点投影的模型上下文窗口计数，仅用于界面提示。 */
  contextWindow?: AgentContextWindow
}

export interface AiTask {
  id: string
  title: string
  messages: AiMessage[]
  updatedAt: number
  threadId?: string
}

const TASKS_KEY_PREFIX = 'biliup.agent.tasks.'
const ACTIVE_TASK_KEY_PREFIX = 'biliup.agent.active_task.'
const FIRST_USE_KEY_PREFIX = 'biliup.agent.first_use.'

function storageKey(prefix: string, scopeKey = 'anonymous') {
  return `${prefix}${encodeURIComponent(scopeKey || 'anonymous')}`
}

export function getConversationFirstUseAt(scopeKey = 'anonymous') {
  try {
    const value = Number(localStorage.getItem(storageKey(FIRST_USE_KEY_PREFIX, scopeKey)))
    return Number.isFinite(value) && value > 0 ? value : undefined
  } catch { return undefined }
}

export function markConversationFirstUse(scopeKey = 'anonymous', at = Date.now()) {
  const existing = getConversationFirstUseAt(scopeKey)
  if (existing) return existing
  try { localStorage.setItem(storageKey(FIRST_USE_KEY_PREFIX, scopeKey), String(at)) } catch { /* 本地时间标记是可选能力 */ }
  return at
}

/** 一个账号只有一个 Agent 对话缓存；项目是每轮 State 输入，不是线程边界。 */
export function conversationScopeKey(accountKey = 'anonymous') {
  return accountKey || 'anonymous'
}

export function taskStatus(task: AiTask): AiMessage['status'] | undefined {
  return [...task.messages].reverse().find(message => message.role === 'assistant')?.status
}

export function taskStatusLabel(task: AiTask) {
  const status = taskStatus(task)
  return status ? ({ running: '运行中', completed: '已完成', unavailable: '不可用', empty: '无结果', partial: '部分完成', error: '失败', interrupted: '等待确认' } as const)[status] : '状态待同步'
}

export function taskScopeLabel(task: AiTask) {
  return '对话'
}

export function readTasks(scopeKey = 'anonymous'): AiTask[] {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(TASKS_KEY_PREFIX, scopeKey)) || '[]')
    if (!Array.isArray(value)) return []
    return value.map(item => {
      if (!item || typeof item !== 'object') return item
      const { scope: _scope, projectId: _projectId, ...task } = item as Record<string, unknown>
      return task
    }) as AiTask[]
  } catch { return [] }
}

function notify(scopeKey = 'anonymous') {
  window.dispatchEvent(new CustomEvent('biliup:ai-state', { detail: { scopeKey } }))
}

export function writeTasks(tasks: AiTask[], scopeKey = 'anonymous') {
  const next = tasks.slice(0, 12)
  try {
    const key = storageKey(TASKS_KEY_PREFIX, scopeKey)
    const serialized = JSON.stringify(next)
    // 值没有变化时不要派发状态事件。React 使用方会通过该事件进行跨标签页同步；
    // 每次持久化都派发会为反馈更新制造循环。
    if (localStorage.getItem(key) === serialized) return
    localStorage.setItem(key, serialized)
  } catch { /* 本地缓存是可选能力 */ }
  notify(scopeKey)
}

export function upsertTask(task: AiTask, scopeKey = 'anonymous'): AiTask[] {
  const current = readTasks(scopeKey)
  const index = current.findIndex(item => item.id === task.id)
  const next = index < 0
    ? [task, ...current]
    : current.map((item, currentIndex) => currentIndex === index ? task : item)
  writeTasks(next, scopeKey)
  return next.slice(0, 12)
}

export function getActiveTaskId(scopeKey = 'anonymous') {
  try { return localStorage.getItem(storageKey(ACTIVE_TASK_KEY_PREFIX, scopeKey)) || undefined } catch { return undefined }
}

export function setActiveTaskId(taskId: string | undefined, scopeKey = 'anonymous') {
  try {
    if (taskId) localStorage.setItem(storageKey(ACTIVE_TASK_KEY_PREFIX, scopeKey), taskId)
    else localStorage.removeItem(storageKey(ACTIVE_TASK_KEY_PREFIX, scopeKey))
  } catch { /* 本地缓存是可选能力 */ }
  notify(scopeKey)
}

/** 注销时只清理当前认证账号的客户端 AI 缓存。 */
export function clearTaskStore(scopeKey = 'anonymous') {
  try {
    const account = scopeKey || 'anonymous'
    const encodedAccount = encodeURIComponent(account)
    const encodedAccountPrefix = encodeURIComponent(`${account}::`)
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index)
      if (!key) continue
      if (key === `${TASKS_KEY_PREFIX}${encodedAccount}` || key === `${ACTIVE_TASK_KEY_PREFIX}${encodedAccount}` || key === `${FIRST_USE_KEY_PREFIX}${encodedAccount}` || key.startsWith(`${TASKS_KEY_PREFIX}${encodedAccountPrefix}`) || key.startsWith(`${ACTIVE_TASK_KEY_PREFIX}${encodedAccountPrefix}`)) localStorage.removeItem(key)
    }
  } catch { /* 本地缓存是可选能力 */ }
  notify(scopeKey)
}
